import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Shield, MessageCircle, HandHeart, IdCard, Users, ArrowRight } from "lucide-react";
import { api, BRAND, CATEGORY_IMAGES } from "@/lib/api";

const CTAS = [
  { to: "/shop", label: "Shop Autism Essentials", icon: Sparkles, testid: "cta-shop" },
  { to: "/id-card", label: "Create Custom ID Card", icon: IdCard, testid: "cta-idcard" },
  { to: "/insurance", label: "Apply for Insurance Support", icon: Shield, testid: "cta-insurance" },
  { to: "/fundraisers/new", label: "Start a Fundraiser", icon: HandHeart, testid: "cta-fundraiser" },
  { to: "/donate", label: "Donate to a Family", icon: HandHeart, testid: "cta-donate" },
  { to: "/membership", label: "Become a Member", icon: Users, testid: "cta-member" },
];

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data));
    api.get("/products").then(({ data }) => setProducts(data.slice(0, 6)));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="starry-hero relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 pt-14 pb-20 grid md:grid-cols-2 gap-10 items-center relative z-10">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur px-3 py-1.5 rounded-full text-xs font-bold text-pink-600 mb-4 ring-1 ring-pink-200">
              <Sparkles className="h-3.5 w-3.5" /> Trusted by autism families
            </div>
            <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-tight">
              Autism Resources, Safety
              <br />
              Products, AAC Devices &amp;
              <br />
              <span className="text-pink-500 pink-glow">Family Support.</span>
            </h1>
            <p className="mt-5 text-lg text-slate-700 max-w-xl">
              A safer, calmer, more supportive world for every autism family — sensory tools, safety products, AAC devices, custom emergency ID cards with QR, resources, donations, and community.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/shop" data-testid="hero-shop-btn" className="btn-pink">Shop Essentials</Link>
              <Link to="/id-card" data-testid="hero-idcard-btn" className="btn-outline-pink">Create ID Card</Link>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
              {["Sensory-Safe", "Calming Colors", "Family-First"].map((t) => (
                <div key={t} className="text-xs text-center bg-white/80 backdrop-blur rounded-xl px-2 py-2 font-semibold text-purple-700 ring-1 ring-purple-100">{t}</div>
              ))}
            </div>
          </div>
          <div className="relative mx-auto">
            <div className="absolute inset-0 blur-3xl bg-pink-300/30 rounded-full" />
            <img src={BRAND.mascot} alt="Zi Zi mascot" className="relative w-full max-w-md rounded-3xl shadow-2xl" />
          </div>
        </div>
        <div className="rainbow-strip h-2" />
      </section>

      {/* CTA GRID */}
      <section className="max-w-7xl mx-auto px-6 -mt-10 relative z-20">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {CTAS.map((c) => (
            <Link key={c.to} to={c.to} data-testid={c.testid} className="card-soft p-4 flex flex-col items-center text-center group">
              <div className="w-10 h-10 rounded-full bg-pink-50 grid place-items-center mb-2 group-hover:bg-pink-100">
                <c.icon className="h-5 w-5 text-pink-500" />
              </div>
              <div className="text-xs font-bold leading-snug text-slate-800">{c.label}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* SHOP BY NEED */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900">Sensory Products for Kids and Adults</h2>
            <p className="text-slate-600 mt-1">13 categories carefully curated for autism families.</p>
          </div>
          <Link to="/shop" data-testid="view-all-shop" className="text-pink-600 font-semibold hidden md:inline-flex items-center gap-1">View all <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {categories.map((c) => (
            <Link key={c.slug} to={`/shop/${c.slug}`} data-testid={`cat-${c.slug}`} className="card-soft overflow-hidden group">
              <div className="aspect-[5/3] overflow-hidden bg-pink-50">
                <img src={CATEGORY_IMAGES[c.slug]} alt={c.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-4">
                <h3 className="font-display font-bold text-slate-900 group-hover:text-pink-600">{c.name}</h3>
                <p className="text-sm text-slate-600 mt-1 line-clamp-2">{c.blurb}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* WATCH & LEARN */}
      <section className="bg-gradient-to-b from-white to-pink-50/40 py-16" data-testid="watch-learn-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
            <div>
              <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900">Watch & Learn</h2>
              <p className="text-slate-600 mt-1">Stories and education from the Zi Zi's World community.</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {[
              { id: "wUQM3z5Pnr0", title: "I Can Be Anything — Autism-Friendly Kids Song", testid: "video-1" },
              { id: "imN-gbBc1h4", title: "Potty Time — Autism-Friendly Potty Training Song", testid: "video-2" },
            ].map((v) => (
              <div key={v.id} data-testid={v.testid} className="card-soft overflow-hidden">
                <div className="aspect-video bg-black">
                  <iframe
                    className="w-full h-full"
                    src={`https://www.youtube.com/embed/${v.id}?rel=0`}
                    title={v.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-display font-bold text-slate-900">{v.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="bg-purple-50/50 py-16">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mb-2">AAC Communication Devices and Speech Support</h2>
          <p className="text-slate-600 mb-6">Family favorites that keep showing up in homes, schools, and clinics.</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {products.map((p) => (
              <Link key={p.product_id} to={`/product/${p.product_id}`} data-testid={`featured-${p.product_id}`} className="card-soft overflow-hidden">
                <div className="aspect-square bg-pink-50 overflow-hidden">
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                </div>
                <div className="p-3">
                  <div className="text-xs font-bold text-slate-800 line-clamp-2">{p.name}</div>
                  <div className="text-pink-600 font-bold mt-1">${p.price.toFixed(2)}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST/INFO STRIP */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <h2 className="font-display text-3xl md:text-4xl font-bold text-slate-900 mb-2">Create a Custom Autism Emergency ID Card With QR Code</h2>
        <p className="text-slate-600 mb-6 max-w-2xl">Plus insurance-friendly autism resources, fundraisers, donations, and community support.</p>
        <div className="grid md:grid-cols-3 gap-6">
        {[
          { icon: IdCard, title: "Custom Autism ID Card", body: "Upload your child's photo, fill the safe-template fields, generate a printable card + QR emergency profile.", to: "/id-card", testid: "info-idcard" },
          { icon: Shield, title: "Autism Safety and Wandering Prevention", body: "GPS trackers, ID bracelets, exit alarms, and a coverage form for AAC, sensory, mobility, or therapy items.", to: "/insurance", testid: "info-insurance" },
          { icon: HandHeart, title: "Fundraisers, Donations and Community Support", body: "Help fund AAC tablets, sensory kits, and emergency essentials.", to: "/donate", testid: "info-donate" },
        ].map((c) => (
          <Link key={c.title} to={c.to} data-testid={c.testid} className="card-soft p-6 group">
            <c.icon className="h-7 w-7 text-pink-500 mb-3" />
            <h3 className="font-display font-bold text-xl text-slate-900 group-hover:text-pink-600">{c.title}</h3>
            <p className="text-slate-600 mt-2 text-sm">{c.body}</p>
            <span className="mt-4 inline-flex items-center gap-1 text-pink-600 font-semibold text-sm">Learn more <ArrowRight className="h-4 w-4" /></span>
          </Link>
        ))}
        </div>
      </section>

      {/* TAGLINE FOOTER STRIP */}
      <section className="rainbow-strip h-2" />
      <section className="bg-gradient-to-b from-pink-50 to-white py-10 text-center">
        <p className="font-display text-2xl md:text-3xl font-bold text-purple-700">
          Accept. <span className="text-pink-500">Understand.</span> <span className="text-blue-500">Support.</span>
        </p>
      </section>
    </div>
  );
}
